import { apiRequest, userAppApiRequest } from './apiWrapper'
import { getApiHeaders } from './deviceRegistrationApi'
import { API_END_POINT, API_METHOD } from '../utils/constants'
import { encryptPayload } from '../../../../apps/super-app/src/crypto/encryptPayload.js'

export async function loginAPI(email, password, calling_code, mobile) {
  const headers = getApiHeaders()
  let encryptedData = {}
  if (email) {
    encryptedData = await encryptPayload({
      email,
      password
    })
  }
  else if (mobile) {
    encryptedData = await encryptPayload({
      mobile,
      calling_code,
      password
    })
  }

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/user/login',
    data: encryptedData,
    headers: {
      ...headers,
    }
  })

  return response
}

export async function googleAuthExchange(idToken) {
  try {
    const headers = getApiHeaders()
    const base64Url = idToken.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(jsonPayload)
    const uid = payload && payload.sub ? payload.sub : undefined

    if (!uid) {
      throw new Error('Could not extract user ID from Google token')
    }

    const encryptedData = await encryptPayload({
      uid,
      id_token: idToken
    })

    const response = await userAppApiRequest({
      method: 'post',
      url: 'user/v1/social/google_login',
      data: encryptedData,
      headers: {
        ...headers,
        'x-platform': 'WEB',
        'User-Agent': navigator.userAgent
      }
    })
    return response
  } catch (e) {
    console.error('Google auth exchange error:', e)
    const parsed = JSON.parse(e.message)
    errorMessage = parsed?.message || errorMessage

    throw new Error(errorMessage)
  }
}

export async function appleAuthExchange(idToken, grantCode) {
  try {
    const headers = getApiHeaders()

    if (!idToken) {
      throw new Error('Missing Apple id_token')
    }

    const base64Url = idToken.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(jsonPayload)
    const uid = payload && payload.sub ? payload.sub : undefined

    if (!uid) {
      throw new Error('Could not extract user ID from Apple token')
    }

    const encryptedData = await encryptPayload({
      uid,
      id_token: idToken,
      grant_code: grantCode
    })

    const response = await userAppApiRequest({
      method: 'post',
      url: 'user/v1/social/apple_login',
      data: encryptedData,
      headers: {
        ...headers,
        'x-platform': 'WEB',
        'User-Agent': navigator.userAgent
      }
    })
    return response
  } catch (e) {
    console.error('Apple auth exchange error:', e)
    let errorMessage = e?.message || 'Apple login failed'
    if (typeof e?.message === 'string') {
      try {
        const parsed = JSON.parse(e.message)
        if (parsed?.message) errorMessage = parsed.message
      } catch (ignore) {}
    }
    throw new Error(errorMessage)
  }
}

export async function uploadProfileImage(file) {
  const headers = getApiHeaders()
  const formData = new window.FormData()
  formData.append('file', file)
  formData.append('intent', 'PROFILE')

  // Remove Content-Type to allow browser to set boundary for FormData
  if (headers['Content-Type']) {
    delete headers['Content-Type']
  }

  const response = await userAppApiRequest({
    method: 'post',
    url: 'asset/v1/asset-manager/temp/upload',
    data: formData,
    headers: {
      ...headers
    }
  })
  return response
}

export async function registerAPI(formData) {
  const headers = getApiHeaders()
  let data = {}

  const isFormData = formData && (
    formData instanceof FormData ||
    (typeof formData.append === 'function') ||
    Object.prototype.toString.call(formData) === '[object FormData]'
  )

  if (isFormData) {
    if (typeof formData.forEach === 'function') {
      formData.forEach((value, key) => {
        data[key] = value
      })
    } else if (typeof formData.entries === 'function') {
      for (const [key, value] of formData.entries()) {
        data[key] = value
      }
    }
  } else {
    data = { ...formData }
  }

  // Handle profile image upload if it's a file
  const imageFile = data.profile_image || data.profile_picture

  if (imageFile instanceof File) {
    try {
      const uploadResp = await uploadProfileImage(imageFile)
      if (uploadResp?.success && uploadResp?.data?.temp_key) {
        data.profile_image = uploadResp.data.temp_key
      } else {
        delete data.profile_image
      }
    } catch (error) {
      console.error('Profile image upload failed:', error)
      delete data.profile_image
    }
  } else if (typeof data.profile_image !== 'string') {
    delete data.profile_image
  }

  // Clean up old key if it exists
  delete data.profile_picture

  const encryptedData = await encryptPayload(data)

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/register/register_with_profile',
    data: encryptedData,
    headers: {
      ...headers,
    }
  })
  return response
}

export async function refreshToken(refresh) {
  const headers = getApiHeaders()
  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/user/refresh_token',
    data: { refresh_token: refresh },
    headers: {
      ...headers,
    }
  })
  return response
}

export async function fetchForgotPasswordApi(email, calling_code, mobile) {
  const headers = getApiHeaders()
  const payload = {}
  if (email) {
    payload.email = email
  } else if (mobile) {
    payload.mobile = mobile
    payload.calling_code = calling_code
  }

  const encryptedData = await encryptPayload(payload)

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/user/forgot_password',
    data: encryptedData,
    headers: {
      ...headers,
    }
  })
  return response
}

export async function resetPasswordApi(
  email,
  new_password,
  confirm_password,
  calling_code,
  mobile
) {
  const headers = getApiHeaders()
  const payload = {
    new_password: new_password,
    confirm_password: confirm_password
  }
  if (email) {
    payload.email = email
  } else if (mobile) {
    payload.mobile = mobile
    payload.calling_code = calling_code
  }

  const encryptedData = await encryptPayload(payload)

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/user/set_forgot_password',
    data: encryptedData,
    headers: {
      ...headers,
    }
  })
  return response
}

export async function verifyEmail(token) {
  const response = await apiRequest({
    method: 'get',
    url: `/verify-email/${{ token }}`
  })
  return response
}

export async function verifyOtpApi(formData) {
  const headers = getApiHeaders()
  const payload = {}

  if (formData instanceof FormData) {
    formData.forEach((value, key) => {
      payload[key] = value
    })
  } else {
    Object.assign(payload, formData)
  }

  const dataToEncrypt = {
    otp: payload.otp,
    intent: payload.intent
  }

  if (payload.email) {
    dataToEncrypt.email = payload.email
  } else if (payload.mobile) {
    dataToEncrypt.mobile = payload.mobile
    dataToEncrypt.calling_code = payload.calling_code
  }

  const encryptedData = await encryptPayload(dataToEncrypt)

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/register/verify_otp_register',
    data: encryptedData,
    headers: {
      ...headers
    }
  })
  return response
}

export async function verifyConset() {
  const response = await apiRequest({
    method: 'get',
    url: `/user/v1/legal/documents/active`
  })
  return response
}

export async function resendOtpApi(email, mobile, calling_code, intent) {
  const header = getApiHeaders()
  const payload = { intent }

  if (email) {
    payload.email = email
  } else if (mobile) {
    payload.mobile = mobile
    payload.calling_code = calling_code
  }

  const encryptedData = await encryptPayload(payload)
  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/register/resend_otp',
    data: encryptedData,
    headers: {
      ...header
    }
  })
  return response
}

export async function changePasswordApi({
  new_password,
  new_password_confirm,
  old_password
}) {
  const headers = getApiHeaders()
  const accessToken = localStorage.getItem('access')
  const data = {
    new_password,
    new_password_confirm
  }

  // Only include old_password if provided
  if (old_password) {
    data.old_password = old_password
  }

  const response = await userAppApiRequest({
    method: API_METHOD.Put,
    url: API_END_POINT.changePassword,
    data,
    headers: {
      ...headers,
      'x-api-token': accessToken
    }
  })
  return response
}

export async function deleteApi() {
  const headers = getApiHeaders()
  const accessToken = localStorage.getItem('access')
  const response = await userAppApiRequest({
    method: API_METHOD.Post,
    url: API_END_POINT.Delete,
    headers: {
      ...headers,
      'x-api-token': accessToken,
    }
  })
  return response
}

export async function updateEmailApi(email) {
  const headers = getApiHeaders()
  const accessToken = localStorage.getItem('access')

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/auth/user_profile/update_email_mobile',
    data: { email: email },
    headers: {
      ...headers,
      'x-api-token': accessToken
    }
  })
  return response
}

export async function updateMobileApi(mobile, calling_code, intent) {
  const headers = getApiHeaders()
  const accessToken = localStorage.getItem('access')
  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/auth/user_profile/update_email_mobile',
    data: { mobile: mobile, calling_code: calling_code, intent: intent },
    headers: {
      ...headers,
      'x-api-token': accessToken
    }
  })
  return response
}

export async function verifyOtpUpdateEmailApi(
  email,
  otp,
  intent = 'update_profile'
) {
  return await verifyOtpApi({ email, otp, intent })
}

export async function verifyOtpUpdateMobileApi(
  mobile,
  calling_code,
  otp,
  intent = 'update_profile'
) {
  return await verifyOtpApi({ mobile, calling_code, otp, intent })
}

export async function verifySignUP(formData) {
  const headers = getApiHeaders()
  let data = {}

  if (formData instanceof FormData) {
    formData.forEach((value, key) => {
      data[key] = value
    })
  } else {
    data = { ...formData }
  }

  // Handle profile image upload if it's a file
  if (data.profile_image instanceof File) {
    try {
      const uploadResp = await uploadProfileImage(data.profile_image)
      if (uploadResp?.success && uploadResp?.data?.temp_key) {
        data.profile_image = uploadResp.data.temp_key
      } else {
        delete data.profile_image
      }
    } catch (error) {
      console.error('Profile image upload failed:', error)
      delete data.profile_image
    }
  } else if (typeof data.profile_image !== 'string') {
    delete data.profile_image
  }

  const encryptedData = await encryptPayload(data)

  const response = await userAppApiRequest({
    method: 'post',
    url: 'user/v1/register/verify_otp_register',
    data: encryptedData,
    headers: {
      ...headers
    }
  })
  return response
}

export async function verifyRecaptcha(data) {
  const response = await apiRequest({
    method: 'post',
    url: 'user/v1/register/recaptcha/verify',
    data
  })
  return response
}

export async function verifyPasswordApi({ password, intent }) {
  const headers = getApiHeaders()
  const accessToken = localStorage.getItem('access')

  const response = await userAppApiRequest({
    method: 'post',
    url: API_END_POINT.verifyPassword,
    data: { password, intent },
    headers: {
      ...headers,
      'x-api-token': accessToken
    }
  })
  return response
}
